import authService from '@/services/zybook-auth-service';
import { createServerClient as createCoreServerClient, joinUrl, type AuthProvider, type ZyServers } from './servers-core';

// Read authService lazily: it imports this module, so it may not be initialised at load time.
const browserAuth: AuthProvider = {
    getAuthToken: () => authService.getAuthToken(),
    refreshAuthToken: () => authService.refreshAuthToken(),
};

/**
 * Creates a browser server client: the shared core client plus XHR upload with progress.
 * @param baseUrl - The base URL for the server
 * @returns Server client object
 */
function createServerClient(baseUrl: string) {
    return {
        ...createCoreServerClient(baseUrl, browserAuth),

        /**
         * Uploads a file with progress tracking using XMLHttpRequest
         * @param endpoint - The endpoint path
         * @param formData - The FormData containing the file
         * @param onProgress - Callback for upload progress updates
         * @param signal - AbortSignal for cancelling the upload
         * @returns Promise resolving to the response data
         */
        uploadWithProgress<T = any>(
            endpoint: string,
            formData: FormData,
            onProgress?: (event: { progress: number }) => void,
            signal?: AbortSignal,
        ): Promise<T> {
            const url = joinUrl(baseUrl, endpoint);
            const authToken = browserAuth.getAuthToken();

            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                // Handle abort signal
                if (signal) {
                    signal.addEventListener('abort', () => {
                        xhr.abort();
                        reject(new DOMException('Upload aborted', 'AbortError'));
                    });
                }

                // Track upload progress
                xhr.upload.addEventListener('progress', event => {
                    if (event.lengthComputable && onProgress) {
                        const progress = Math.round((event.loaded / event.total) * 100);
                        onProgress({ progress });
                    }
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        try {
                            const response = JSON.parse(xhr.responseText) as T & {
                                success?: boolean;
                                error?: { message: string };
                            };
                            if (response.success === false) {
                                reject(new Error(response.error?.message ?? 'Unknown error'));
                            } else {
                                resolve(response);
                            }
                        } catch {
                            reject(new Error('Failed to parse response'));
                        }
                    } else {
                        reject(new Error(`Request failed with status ${xhr.status}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error('Network error during upload'));
                });

                xhr.addEventListener('abort', () => {
                    reject(new DOMException('Upload aborted', 'AbortError'));
                });

                xhr.open('POST', url);
                xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
                xhr.send(formData);
            });
        },
    };
}

// Create server clients
export const zyAuthor = createServerClient(import.meta.env.VITE_ZYAUTHOR_URL);
export const zyServer = createServerClient(import.meta.env.VITE_ZYSERVER_URL);
export const zyServer2 = createServerClient(import.meta.env.VITE_ZYSERVER2_URL);
export const itLabsServer = createServerClient(import.meta.env.VITE_ITLABS_SERVER_URL);
export const zyStreamlit = createServerClient(import.meta.env.VITE_STREAMLIT_SERVER_URL);

export const browserServers: ZyServers = { zyAuthor, zyServer, zyServer2, itLabsServer, zyStreamlit };
